import { Badge, cn } from "@devhop/ui";
import {
  Building2Icon,
  PhoneIcon,
  UserIcon,
  UsersIcon,
  WalletIcon,
} from "lucide-react";
import type { OutsourcePayeeType } from "../../domain/contracts";

const PAYEE_LABELS = {
  individual: "ບຸກຄົນ",
  team: "ທີມ / ບໍລິສັດ",
} as const;

const CATEGORY_TONE: Record<string, string> = {
  kol: "border-violet-200 bg-violet-500/10 text-violet-800 dark:text-violet-200",
  media: "border-indigo-200 bg-indigo-500/10 text-indigo-800 dark:text-indigo-200",
  photography:
    "border-sky-200 bg-sky-500/10 text-sky-800 dark:text-sky-200",
  videography:
    "border-cyan-200 bg-cyan-500/10 text-cyan-800 dark:text-cyan-200",
  live_stream:
    "border-orange-200 bg-orange-500/10 text-orange-800 dark:text-orange-200",
  production_house:
    "border-blue-200 bg-blue-500/10 text-blue-800 dark:text-blue-200",
  mc_host:
    "border-amber-200 bg-amber-500/10 text-amber-800 dark:text-amber-200",
  styling:
    "border-pink-200 bg-pink-500/10 text-pink-800 dark:text-pink-200",
  other: "border-border bg-muted/50 text-muted-foreground",
};

export function formatOutsourceMoney(value: number) {
  return `${new Intl.NumberFormat("lo-LA").format(value)} ກີບ`;
}

export function OutsourceCategoryBadge({
  name,
  slug,
  className,
}: {
  name?: string | null;
  slug?: string | null;
  className?: string;
}) {
  const label = name ?? slug ?? "—";
  const tone = slug ? (CATEGORY_TONE[slug] ?? CATEGORY_TONE.other) : CATEGORY_TONE.other;

  return (
    <Badge
      variant="outline"
      className={cn("max-w-[10rem] truncate font-normal", tone, className)}
      title={label}
    >
      {label}
    </Badge>
  );
}

export function OutsourcePayeeTypeBadge({
  payeeType,
  className,
}: {
  payeeType: OutsourcePayeeType;
  className?: string;
}) {
  const isTeam = payeeType === "team";
  return (
    <Badge
      variant="outline"
      className={cn(
        "gap-1 font-normal",
        isTeam
          ? "border-blue-200 bg-blue-500/5 text-blue-800 dark:text-blue-200"
          : "border-emerald-200 bg-emerald-500/5 text-emerald-800 dark:text-emerald-200",
        className,
      )}
    >
      {isTeam ? (
        <UsersIcon className="size-3 shrink-0" />
      ) : (
        <UserIcon className="size-3 shrink-0" />
      )}
      {PAYEE_LABELS[payeeType]}
    </Badge>
  );
}

export function OutsourcePayeeCell({
  displayName,
  payeeType,
  contactName,
  roleLabel,
  deliverableSummary,
  notes,
  subtitle,
}: {
  displayName: string;
  payeeType: OutsourcePayeeType;
  contactName?: string | null;
  roleLabel?: string | null;
  deliverableSummary?: string | null;
  notes?: string | null;
  subtitle?: string | null;
}) {
  const isTeam = payeeType === "team";
  const secondary =
    subtitle ??
    (isTeam && contactName ? contactName : null) ??
    roleLabel ??
    deliverableSummary ??
    notes;

  return (
    <div className="flex min-w-[12rem] items-start gap-3 py-1">
      <div
        className={cn(
          "flex size-9 shrink-0 items-center justify-center rounded-lg border",
          isTeam
            ? "border-blue-200 bg-blue-500/10 text-blue-700 dark:text-blue-200"
            : "border-emerald-200 bg-emerald-500/10 text-emerald-700 dark:text-emerald-200",
        )}
      >
        {isTeam ? (
          <Building2Icon className="size-4" />
        ) : (
          <UserIcon className="size-4" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-sm leading-snug">{displayName}</p>
        {secondary ? (
          <p className="mt-0.5 line-clamp-2 text-muted-foreground text-xs leading-relaxed">
            {secondary}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export function OutsourcePhoneCell({ phone }: { phone?: string | null }) {
  if (!phone?.trim()) {
    return <span className="text-muted-foreground text-sm">—</span>;
  }

  return (
    <div className="flex items-center gap-2 py-1">
      <span className="flex size-7 shrink-0 items-center justify-center rounded-md border bg-muted/40">
        <PhoneIcon className="size-3.5 text-muted-foreground" />
      </span>
      <span className="font-medium text-sm tabular-nums">{phone}</span>
    </div>
  );
}

export function OutsourceBankCell({
  bankName,
  bankAccount,
  bankAccountName,
}: {
  bankName?: string | null;
  bankAccount?: string | null;
  bankAccountName?: string | null;
}) {
  if (!bankAccount?.trim() && !bankName?.trim()) {
    return (
      <span className="inline-flex items-center gap-1.5 text-muted-foreground text-sm">
        <WalletIcon className="size-3.5" />
        —
      </span>
    );
  }

  return (
    <div className="flex min-w-[10rem] items-start gap-2 py-1">
      <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md border bg-muted/40">
        <WalletIcon className="size-3.5 text-muted-foreground" />
      </span>
      <div className="min-w-0 space-y-0.5">
        {bankName ? (
          <p className="font-medium text-sm leading-tight">{bankName}</p>
        ) : null}
        {bankAccount ? (
          <p className="font-mono text-muted-foreground text-xs tabular-nums tracking-tight">
            {bankAccount}
          </p>
        ) : null}
        {bankAccountName ? (
          <p className="line-clamp-1 text-muted-foreground text-xs">
            {bankAccountName}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export function OutsourceAmountCell({
  amount,
  emphasize = true,
}: {
  amount: number;
  emphasize?: boolean;
}) {
  return (
    <div className="py-1 text-right">
      <p
        className={cn(
          "tabular-nums leading-tight",
          emphasize ? "font-bold text-sm" : "font-medium text-sm",
        )}
      >
        {formatOutsourceMoney(amount)}
      </p>
    </div>
  );
}

export function OutsourceActiveBadge({ isActive }: { isActive: boolean }) {
  return (
    <Badge
      variant={isActive ? "outline" : "secondary"}
      className={cn(
        "gap-1.5 font-normal",
        isActive &&
          "border-emerald-200 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200",
      )}
    >
      <span
        className={cn(
          "size-1.5 rounded-full",
          isActive ? "bg-emerald-500" : "bg-muted-foreground/50",
        )}
      />
      {isActive ? "ໃຊ້ງານ" : "ປິດໃຊ້"}
    </Badge>
  );
}

export const OUTSOURCE_STATUS_LABELS = {
  planned: "ວາງແຜນ",
  partially_paid: "ຈ່າຍບາງງວດ",
  paid: "ຈ່າຍແລ້ວ",
  cancelled: "ຍົກເລີກ",
} as const;

export const OUTSOURCE_STATUS_VARIANTS = {
  planned: "secondary",
  partially_paid: "outline",
  paid: "default",
  cancelled: "destructive",
} as const;

export function OutsourceRowStatusBadge({
  status,
}: {
  status: keyof typeof OUTSOURCE_STATUS_LABELS;
}) {
  return (
    <Badge variant={OUTSOURCE_STATUS_VARIANTS[status]}>
      {OUTSOURCE_STATUS_LABELS[status]}
    </Badge>
  );
}
